<template>
  <div class="bs-portal">
    <!-- ============ 顶部说明 ============ -->
    <div class="portal-hero">
      <div class="hero-left">
        <h1>月度数据驾驶舱大屏</h1>
        <p>月底冲刺展示用。先在此处勾选要展示的<b>区域 / 校区</b>与<b>收款账户</b>，再一键全屏打开，
          电视端即按所选范围轮播播放。</p>
      </div>
      <div class="hero-right">
        <el-button type="primary" size="large" @click="openFullScreen">
          <span class="btn-ico">⛶</span> 全屏打开大屏
        </el-button>
        <el-button size="large" @click="copyLink">复制直连地址</el-button>
      </div>
    </div>

    <!-- ============ 选项加载失败常驻提示 ============
         与大屏抽屉同款：区域树/收款账户拉不到时，页面看起来只是"没有可选内容"，
         运营会以为"系统里就没有这些校区/账户"，而不是"接口挂了"。
         必须把失败摆到脸上，并给一个自助重试入口。 -->
    <div class="opt-error" v-if="optionsError">
      <span class="oe-icon">⚠</span>
      <span class="oe-text">{{ optionsError }}</span>
      <button class="oe-retry" @click="reloadOptions">重新加载</button>
    </div>

    <!-- ============ 筛选区 ============ -->
    <el-card class="portal-card" shadow="never">
      <template #header>
        <div class="card-head">
          <span class="card-title">① 选择展示范围</span>
          <span class="card-sub">不选任何区域 = 展示全部校区</span>
        </div>
      </template>

      <el-form label-width="120px" label-position="left">
        <!-- 业务线 -->
        <el-form-item label="业务线">
          <el-checkbox-group v-model="form.flags" @change="onFlagsChange">
            <el-checkbox :label="1">中招</el-checkbox>
            <el-checkbox :label="2">少年</el-checkbox>
            <el-checkbox :label="4">高考</el-checkbox>
            <el-checkbox :label="5">幼儿</el-checkbox>
          </el-checkbox-group>
          <div class="tip">可多选；不勾选 = 全部业务线（勾选后区域树只显示该业务线下的校区）</div>
        </el-form-item>

        <!-- 区域 / 校区：多级、跨城市、可混选 -->
        <el-form-item label="区域 / 校区">
          <div class="area-row">
            <el-cascader
                v-model="form.areaIds"
                :options="areaTree"
                :props="cascaderProps"
                show-checked-strategy="parent"
                collapse-tags
                collapse-tags-tooltip
                clearable
                filterable
                :filter-method="areaFilterMethod"
                placeholder="可搜索校区名（也支持输入区域名）"
                class="area-cascader" />
            <el-radio-group v-model="selectMode" class="mode-switch" @change="onModeChange">
              <el-radio-button label="multi">多选</el-radio-button>
              <el-radio-button label="single">单选</el-radio-button>
            </el-radio-group>
          </div>
          <div class="tip">
            <b>勾选规则</b>：层级为 <b>省 → 市 → 区/县 → 校区</b>（勾了业务线后，
            树上只保留该业务线<b>确实有校区</b>的路径）。
            <b>勾上级＝自动全选其下全部下级</b>，取消上级则下级一并取消；
            下级全部选中时上级自动打勾，只选了一部分时上级显示<span class="tip-half">半选</span>。
            支持<b>跨城市、跨层级混选</b>，也可只勾单个校区。
            <span v-if="selectMode === 'single'" class="tip-warn">（单选模式下每次只能选一个节点）</span>
          </div>
          <div v-if="treeStat" class="sel-summary">
            当前范围：<b>{{ treeStat.levelCount }} 级</b> ·
            省 {{ treeStat.l1 }} / 市 {{ treeStat.l2 }} / 区县 {{ treeStat.l3 }} / 校区 {{ treeStat.campus }}
          </div>
          <div v-if="selectedSummary" class="sel-summary">已选：{{ selectedSummary }}</div>
        </el-form-item>

        <!-- 收款账户 -->
        <el-form-item label="收款账户">
          <el-select v-model="form.paymentIds" multiple collapse-tags collapse-tags-tooltip
                     clearable filterable placeholder="全部收款账户（可多选）" class="full-w">
            <el-option v-for="p in paymentOptions" :key="p.paymentId"
                       :label="p.mchName" :value="p.paymentId">
              <span class="opt-name">{{ p.mchName }}</span>
              <span class="opt-region" :class="{ 'opt-region-missing': p.noRegion }">
                {{ p.regionName }}
              </span>
            </el-option>
          </el-select>
          <div class="tip">按商户维度筛选收款数据；支持跨区域多选</div>
        </el-form-item>

        <!-- 统计月份 -->
        <el-form-item label="统计月份">
          <el-date-picker v-model="form.month" type="month" value-format="YYYY-MM"
                          placeholder="默认当前月" class="full-w" />
        </el-form-item>

        <!-- 标题 -->
        <el-form-item label="大屏标题">
          <el-input v-model="form.prefix" maxlength="20" show-word-limit clearable
                    placeholder="如：南阳 / 中招事业部（默认「焱火体育」）" class="full-w" />
          <div class="tip">
            自动拼成「<b>{{ form.prefix || '焱火体育' }} · 月度战报</b>」；
            若填「完整标题」则优先使用完整标题。
          </div>
        </el-form-item>
        <el-form-item label="完整标题">
          <el-input v-model="form.title" maxlength="30" show-word-limit clearable
                    placeholder="留空则用上面的前缀自动拼接" class="full-w" />
        </el-form-item>
      </el-form>

      <div class="portal-actions">
        <el-button type="primary" @click="openFullScreen">应用并全屏打开</el-button>
        <el-button @click="resetForm">重置</el-button>
      </div>
    </el-card>

    <!-- ============ 直连地址说明 ============ -->
    <el-card class="portal-card" shadow="never">
      <template #header>
        <div class="card-head">
          <span class="card-title">② 电视端直连（可免登录挂载）</span>
        </div>
      </template>
      <p class="link-desc">把下面地址配到电视浏览器首页/书签，开机即自动进入大屏：</p>
      <div class="link-box">
        <code>{{ fullUrl }}</code>
        <el-button size="small" @click="copyLink">复制</el-button>
      </div>
      <el-table :data="paramDocs" size="small" border class="doc-table">
        <el-table-column prop="name" label="参数" width="130" />
        <el-table-column prop="sample" label="示例" width="180" />
        <el-table-column prop="desc" label="说明" />
      </el-table>
    </el-card>
  </div>
</template>

<script>
import { getPaymentOptions, getBattleAreaTree } from '@/api/battleScreen';
import { expandToLeaves, collapseToTopNodes, toIdArray, areaFilterMethod } from '@/utils/areaTree';

export default {
  name: 'BattleScreenPortal',
  data() {
    return {
      form: { flags: [], areaIds: [], paymentIds: [], month: '', prefix: '', title: '' },
      areaTree: [],
      areaNameMap: {},
      paymentOptions: [],
      // 选项加载失败标记（分别记，最后合成一句给用户看，便于指出到底哪一项挂了）
      treeError: '',
      paymentError: '',
      selectMode: 'multi',
      paramDocs: [
        { name: 'prefix', sample: '南阳', desc: '标题前缀，自动拼成「南阳 · 月度战报」' },
        { name: 'title', sample: '中招事业部战报', desc: '直接指定完整标题（优先级高于 prefix）' },
        { name: 'flags', sample: '1,2', desc: '业务线 1中招 2少年 4高考 5幼儿' },
        { name: 'areaIds', sample: '629', desc: '预置区域/校区 id，可传任意层级（省/市/区县/校区），后端会自动展开为其下全部校区' },
        { name: 'paymentIds', sample: '32,33', desc: '预置勾选的收款账户 id（region_payment.id）' },
        { name: 'month', sample: '2026-09', desc: '统计月份，默认当前月' }
      ]
    };
  },
  computed: {
    /** 选项加载失败的合并提示（空串=一切正常，不显示提示条） */
    optionsError() {
      const failed = [];
      if (this.treeError) failed.push('区域/校区');
      if (this.paymentError) failed.push('收款账户');
      return failed.length
        ? ('筛选选项加载失败：' + failed.join('、') + '，请检查服务后重试')
        : '';
    },
    cascaderProps() {
      return {
        multiple: this.selectMode === 'multi',
        value: 'id',
        label: 'name',
        children: 'children',
        /*
         * 多选：**不启用 checkStrictly**，交给 el-cascader 原生父子联动 ——
         *   · 勾「郑州市」→ 其下区/县、校区全部自动打勾，且郑州自身显示对勾
         *   · 取消「郑州市」→ 其下全部一并取消
         *   · 下级全选 → 上级自动打勾；只选一部分 → 上级显示半选
         * 此时 v-model 拿到的是**叶子校区 id 数组**（Element Plus 的
         * calculateCheckedValue 在 checkStrictly=false 时传 leafOnly=true）。
         * 后端 expandCampusIds 正是按叶子校区收敛口径，前后端天然一致。
         *
         * 单选：没有联动语义，开启 checkStrictly 允许直接点到「城市」这一层。
         */
        checkStrictly: this.selectMode === 'single',
        emitPath: false
      };
    },
    /** 已选校区数（多选时 v-model 是叶子校区 id 数组；勾城市会自动展开成其下全部校区） */
    selectedSummary() {
      if (this.selectMode === 'single') {
        const id = toIdArray(this.form.areaIds)[0];
        return id ? (this.areaNameMap[id] || ('#' + id)) : '';
      }
      const n = toIdArray(this.form.areaIds).length;
      return n ? `${n} 个校区` : '';
    },
    /**
     * 树形自检：把后端返回的 areaTree 按「层级」统计节点数。
     * 作用是让"结构不是多级的"这类问题一眼可见 ——
     * 若 l1 有几百个而 l2/l3 为 0，说明建树退化成了平铺（历史 bug 的典型症状）。
     */
    treeStat() {
      const l1 = (this.areaTree || []).length;
      if (!l1) return null;
      let l2 = 0, l3 = 0, campus = 0, maxDepth = 1;
      const walk = (nodes, depth) => {
        if (depth > maxDepth) maxDepth = depth;
        (nodes || []).forEach(n => {
          const ch = n.children || [];
          if (depth === 2) l2 += 1;
          if (depth === 3) l3 += 1;
          if (!ch.length) campus += 1;
          if (ch.length) walk(ch, depth + 1);
        });
      };
      walk(this.areaTree, 1);
      return { l1, l2, l3, campus, levelCount: maxDepth };
    },
    fullUrl() {
      const base = `${window.location.origin}/battleScreen`;
      const qs = this.buildQuery();
      return qs ? `${base}?${qs}` : base;
    }
  },
  created() {
    // 读 URL 预置参数（便于电视端直连 / 复制分享）
    const q = new URLSearchParams(window.location.search || '');
    const flags = q.get('flags');
    const areaIds = q.get('areaIds');
    const paymentIds = q.get('paymentIds');
    const month = q.get('month');
    const prefix = q.get('prefix');
    const title = q.get('title');
    if (flags) this.form.flags = flags.split(',').map(Number).filter(n => n > 0);
    if (areaIds) this.form.areaIds = areaIds.split(',').map(Number).filter(n => n > 0);
    if (paymentIds) this.form.paymentIds = paymentIds.split(',').map(Number).filter(n => n > 0);
    if (month) this.form.month = month;
    if (prefix) this.form.prefix = prefix;
    if (title) this.form.title = title;

    this.loadTree();
    this.loadPaymentOptions();
  },
  methods: {
    buildNameMap(nodes, map) {
      (nodes || []).forEach(n => {
        map[n.id] = n.name;
        if (n.children && n.children.length) this.buildNameMap(n.children, map);
      });
    },
    async loadTree() {
      try {
        // 后端 areaTree(@RequestBody List<Integer> flags)：业务线变化后重新拉树
        const res = await getBattleAreaTree(this.form.flags || []);
        if (res && res.code === 200) {
          this.treeError = '';
          this.areaTree = res.data || [];
          const map = {};
          this.buildNameMap(this.areaTree, map);
          this.areaNameMap = map;
          return this.syncAreaIds();
        }
        this.treeError = '接口未返回有效数据';
      } catch (e) {
        // 🔴 不允许静默吞掉：失败必须留痕，否则页面表现成"系统里没有校区"，无从排查
        this.treeError = '接口异常';
      }
      return 0;
    },
    async loadPaymentOptions() {
      try {
        // 后端 paymentOptions() 无参
        const res = await getPaymentOptions();
        if (res && res.code === 200) {
          this.paymentError = '';
          this.paymentOptions = res.data || [];
        } else {
          this.paymentError = '接口未返回有效数据';
        }
      } catch (e) {
        this.paymentError = '接口异常';
      }
    },
    /** 提示条上的「重新加载」：两个选项接口一起重取 */
    async reloadOptions() {
      await Promise.all([this.loadTree(), this.loadPaymentOptions()]);
      if (!this.optionsError && this.$message) {
        this.$message.success('筛选选项已重新加载');
      }
    },
    /**
     * 把当前已选区域与**最新区域树**对齐，返回被丢弃的无效 id 数。
     *
     *  - 多选：v-model 只认叶子校区 id（checkStrictly:false），
     *    而 URL 里可能带中间层 id（文档示例 `?areaIds=629` 的 629 就是市级节点）
     *    → 展开成叶子，否则会出现"数据已按该市过滤、树上却什么都没勾"的错位。
     *  - 单选：允许停在任意层级，只丢弃树里不存在的 id。
     */
    syncAreaIds() {
      const cur = toIdArray(this.form.areaIds);
      if (!cur.length) return 0;
      const known = cur.filter(id => this.areaNameMap[id] !== undefined);
      const next = this.selectMode === 'multi' ? expandToLeaves(this.areaTree, known) : known;
      this.form.areaIds = this.selectMode === 'multi' ? next : (next[0] ?? null);
      return cur.length - known.length;
    },
    async onFlagsChange() {
      // 业务线变化 → 区域树重取（树上只剩该业务线有校区的路径），
      // 已选区域里可能有已被过滤掉的 → 收敛，避免"选了却不出数"。
      const dropped = await this.loadTree();
      if (dropped > 0) {
        this.$message.info(`业务线变化，已清理 ${dropped} 个不在当前范围内的已选区域`);
      }
    },
    onModeChange() {
      // 切换单选/多选时清空，避免残留值造成歧义
      this.form.areaIds = [];
    },

    areaFilterMethod,
    buildQuery() {
      const q = new URLSearchParams();
      if (this.form.flags && this.form.flags.length) q.set('flags', this.form.flags.join(','));
      // 🔴 折叠成"最高代表节点"再写 URL：
      //    多选时 v-model 是叶子校区 id（勾一个市可能 80+、勾一个省 600+ 个），
      //    直接拼进去会让直连地址达到数 KB —— 电视端老 WebView / 网关可能截断，
      //    静默退化成"全量"，与所选范围不符。
      //    后端 expandCampusIds 本就按任意层级展开，折叠后语义完全不变。
      const areas = collapseToTopNodes(this.areaTree, this.form.areaIds);
      if (areas.length) q.set('areaIds', areas.join(','));
      if (this.form.paymentIds && this.form.paymentIds.length) q.set('paymentIds', this.form.paymentIds.join(','));
      if (this.form.month) q.set('month', this.form.month);
      // 标题：完整标题优先，否则用前缀（大屏侧按同样优先级解析）
      if (this.form.title) q.set('title', this.form.title);
      else if (this.form.prefix) q.set('prefix', this.form.prefix);
      return q.toString();
    },
    openFullScreen() {
      const qs = this.buildQuery();
      const url = qs ? `/battleScreen?${qs}` : '/battleScreen';
      // 用 <a target=_blank> 打开，避免被 keep-alive 路由逻辑拦截
      const a = document.createElement('a');
      a.href = url;
      a.target = '_blank';
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    },
    async copyLink() {
      const text = this.fullUrl;
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(text);
        } else {
          const ta = document.createElement('textarea');
          ta.value = text;
          ta.style.position = 'fixed';
          ta.style.opacity = '0';
          document.body.appendChild(ta);
          ta.select();
          document.execCommand('copy');
          document.body.removeChild(ta);
        }
        this.$message.success('已复制直连地址');
      } catch (e) {
        this.$message.warning('复制失败，请手动选择地址复制');
      }
    },
    resetForm() {
      this.form = { flags: [], areaIds: [], paymentIds: [], month: '', prefix: '', title: '' };
      this.selectMode = 'multi';
      // 业务线清空后区域树要跟着回到"全量路径"，否则选框显示未勾业务线、
      // 树上却仍只剩上一次业务线的路径。
      this.loadTree();
    }
  }
};
</script>

<style scoped>
.bs-portal { padding: 4px 2px 40px; }

/* 选项加载失败提示条（与大屏抽屉的 .opt-error 视觉一致，形成统一的"故障语言"） */
.opt-error {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  padding: 10px 14px;
  border-radius: 10px;
  font-size: 13px;
  color: #ffd9cf;
  background: linear-gradient(90deg, rgba(255, 77, 109, 0.20), rgba(255, 120, 80, 0.12));
  border: 1px solid rgba(255, 110, 80, 0.45);
}
.opt-error .oe-icon { color: #ff8a68; font-size: 15px; flex: none; }
.opt-error .oe-text { flex: 1; line-height: 1.5; }
.opt-error .oe-retry {
  flex: none;
  padding: 4px 12px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 12px;
  color: #fff;
  background: rgba(255, 110, 80, 0.38);
  border: 1px solid rgba(255, 110, 80, 0.6);
  transition: all 0.18s;
}
.opt-error .oe-retry:hover { background: rgba(255, 110, 80, 0.58); }

.portal-hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 22px 26px;
  margin-bottom: 16px;
  border-radius: 10px;
  background: linear-gradient(135deg, #1e1b2e 0%, #2d2640 60%, #3a2f52 100%);
  color: #fff;
}
.hero-left h1 { margin: 0 0 8px; font-size: 22px; font-weight: 600; letter-spacing: .5px; }
.hero-left p { margin: 0; font-size: 13px; line-height: 1.7; color: rgba(255,255,255,.78); max-width: 780px; }
.hero-left b { color: #d8b4fe; }
.hero-right { display: flex; gap: 10px; flex-shrink: 0; }
.btn-ico { margin-right: 4px; font-size: 15px; }

.portal-card { margin-bottom: 16px; border-radius: 10px; }
.card-head { display: flex; align-items: baseline; gap: 12px; }
.card-title { font-size: 15px; font-weight: 600; color: #303133; }
.card-sub { font-size: 12px; color: #909399; }

.full-w { width: 100%; }
.area-row { display: flex; gap: 12px; align-items: center; width: 100%; }
.area-cascader { flex: 1; }
.mode-switch { flex-shrink: 0; }

.tip { margin-top: 6px; font-size: 12px; color: #909399; line-height: 1.6; }
.tip b { color: #606266; }
.tip-warn { color: #e6a23c; }
.tip-half { color: #6a4fa3; }
.sel-summary {
  margin-top: 8px; padding: 6px 10px; border-radius: 6px;
  background: #f4f0fb; border: 1px solid #e2d8f3;
  font-size: 12px; color: #6a4fa3; line-height: 1.6;
}

.opt-name { margin-right: 10px; }
.opt-region { float: right; font-size: 12px; color: #909399; }
.opt-region-missing { color: #e6a23c; font-style: italic; }

.portal-actions { padding-top: 4px; }

.link-desc { margin: 0 0 10px; font-size: 13px; color: #606266; }
.link-box {
  display: flex; align-items: center; gap: 10px;
  padding: 10px 12px; margin-bottom: 14px;
  background: #f7f8fa; border: 1px solid #e4e7ed; border-radius: 6px;
}
.link-box code { flex: 1; font-size: 12.5px; color: #476582; word-break: break-all; }
.doc-table { margin-top: 4px; }
</style>
